import {useState} from "react";
import {Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Button} from "@/components/ui/button.tsx";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {toast} from "sonner";
import {useGetActivitiesQuery} from "@/store/ticketApi.ts";
import {useCreateChipMutation} from "@/store/chipAssignmentApi.ts";
import {ChipDetail, CreateChipPayload} from "@/types/chipAssignment";

interface CreateChipModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated: (chip: ChipDetail) => void;
}

// מודאל יצירת Chip חדש (שדות המודל — ראו plan 4.B.2).
export function CreateChipModal({open, onOpenChange, onCreated}: CreateChipModalProps) {
    const {data: activities = []} = useGetActivitiesQuery();
    const [createChip, {isLoading}] = useCreateChipMutation();

    const [internalCode, setInternalCode] = useState("");
    const [externalNumber, setExternalNumber] = useState("");
    const [serialNumber, setSerialNumber] = useState("");
    const [activityId, setActivityId] = useState<string>("");
    const [expirationDate, setExpirationDate] = useState("");
    const [tz, setTz] = useState("");
    const [phone, setPhone] = useState("");

    function resetForm() {
        setInternalCode("");
        setExternalNumber("");
        setSerialNumber("");
        setActivityId("");
        setExpirationDate("");
        setTz("");
        setPhone("");
    }

    async function handleSubmit() {
        if (!internalCode.trim()) {
            toast.error("יש להזין קוד פנימי");
            return;
        }

        const payload: CreateChipPayload = {
            internal_code: internalCode.trim(),
            external_number: externalNumber.trim() || undefined,
            serial_number: serialNumber.trim() ? Number(serialNumber.trim()) : undefined,
            activity_id: activityId ? Number(activityId) : undefined,
            expiration_date: expirationDate || undefined,
            tz: tz.trim() || undefined,
            phones: phone.trim() ? [phone.trim()] : undefined,
        };

        try {
            const chip = await createChip(payload).unwrap();
            toast.success("הכרטיס נוצר בהצלחה");
            resetForm();
            onOpenChange(false);
            onCreated(chip);
        } catch (err: unknown) {
            const msg = (err as {data?: {msg?: string}})?.data?.msg || "שגיאה ביצירת הכרטיס";
            toast.error(msg);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>יצירת כרטיס חדש</DialogTitle>
                </DialogHeader>
                <DialogBody className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-base font-medium">קוד פנימי *</Label>
                            <Input
                                value={internalCode}
                                onChange={(e) => setInternalCode(e.target.value)}
                                className="text-base h-11"
                                disabled={isLoading}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-base font-medium">קוד חיצוני</Label>
                            <Input
                                value={externalNumber}
                                onChange={(e) => setExternalNumber(e.target.value)}
                                className="text-base h-11"
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="space-y-4 border-t border-gray-200 pt-4">
                        <div className="text-base font-medium text-muted-foreground">פרטים נוספים</div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-base font-medium">מספר סידורי</Label>
                                <Input
                                    type="number"
                                    value={serialNumber}
                                    onChange={(e) => setSerialNumber(e.target.value)}
                                    className="text-base h-11"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-base font-medium">תוקף</Label>
                                <Input
                                    type="date"
                                    value={expirationDate}
                                    onChange={(e) => setExpirationDate(e.target.value)}
                                    className="text-base h-11"
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-base font-medium">פעילות</Label>
                            <Select value={activityId} onValueChange={setActivityId} disabled={isLoading}>
                                <SelectTrigger className="w-full text-base h-11">
                                    <SelectValue placeholder="בחר פעילות (אופציונלי)" />
                                </SelectTrigger>
                                <SelectContent>
                                    {activities.map((activity) => (
                                        <SelectItem key={activity.id} value={String(activity.id)} className="text-base">
                                            {activity.display_name || activity.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-base font-medium">ת.ז. בעלים</Label>
                                <Input
                                    value={tz}
                                    onChange={(e) => setTz(e.target.value)}
                                    className="text-base h-11"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-base font-medium">טלפון בעלים</Label>
                                <Input
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="text-base h-11"
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button onClick={handleSubmit} disabled={isLoading} className="text-base h-11">
                        {isLoading ? "יוצר..." : "צור כרטיס"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
